import React, { useCallback } from 'react';
import { useTranslation } from '@shared/core';
import { useCardTelemetry } from '@apps/card/Card.telemetry';
import { CategoryMegaNavMobile } from './CategoryMegaNavMobile';
import { Bar, BarItem, MobileFallback, Panel, Root, TriggerButton, TriggerLink } from './CategoryMegaNav.styles';
import { MegaNavPanel } from './MegaNavPanel';
import type { MegaNavLinkClickHandler } from './MegaNavPanel';
import type { MegaNavSurface } from './CategoryMegaNavMobile';
import { MegaNavSeoLinks } from './MegaNavSeoLinks';
import { useCategoryMegaNav } from './useCategoryMegaNav';
import { useResolvedMegaNav } from './useResolvedMegaNav';
import type { ResolvedMegaNavSlot } from './useResolvedMegaNav';

/**
 * Expanded (Minted-style) stationery gallery nav: 5 hover slots, each opening a
 * multi-column panel of deep links into the gallery. Below `md` the same slots render
 * as a scrollable strip that opens a bottom sheet (`CategoryMegaNavMobile`).
 * Gated by `printExpandedGalleryNav` via `CategoryNavSwitch`.
 */
export const CategoryMegaNav = () => {
  const { tq } = useTranslation('stationery');
  const { buttonInteracted } = useCardTelemetry();
  const slots = useResolvedMegaNav();
  const { openSlotId, getRootProps, getTriggerProps, getPanelProps } = useCategoryMegaNav();

  const trackCategoryView = useCallback(
    (slot: ResolvedMegaNavSlot, source: MegaNavSurface = 'megaNav') => {
      if (slot.link?.category) {
        buttonInteracted({ name: 'categoryNav_viewCategoryDesigns', targetStationeryTemplateCategory: slot.link.category, source });
      }
    },
    [buttonInteracted],
  );

  const handleLinkClick = useCallback<MegaNavLinkClickHandler>(
    (link, { slot, column }) => {
      buttonInteracted({
        name: 'categoryNav_megaNavLinkClicked',
        slotId: slot.id,
        columnId: column?.id,
        linkId: link.id,
        targetStationeryTemplateCategory: link.category,
        facet: link.facet,
        format: link.format,
        href: link.href,
      });
    },
    [buttonInteracted],
  );

  return (
    <>
      <MobileFallback>
        <CategoryMegaNavMobile
          slots={slots}
          onLinkClick={handleLinkClick}
          onCategoryView={trackCategoryView}
        />
      </MobileFallback>
      <Root
        aria-label={tq`Category navigation`}
        data-testid="category-mega-nav"
        {...getRootProps()}
      >
        <Bar data-testid="category-mega-nav-bar">
          {slots.map(slot => {
            const triggerProps = getTriggerProps({ id: slot.id, hasLink: !!slot.link });
            const isOpen = openSlotId === slot.id;
            return (
              <BarItem key={slot.id}>
                {/* The open panel sits right after its trigger in the DOM so Tab moves into it. */}
                {slot.link ? (
                  <TriggerLink
                    to={slot.link.href}
                    $isActive={slot.isActive}
                    $isOpen={isOpen}
                    aria-current={slot.isActive ? 'page' : undefined}
                    data-testid={`category-mega-nav-slot-${slot.id}`}
                    {...triggerProps}
                    onClick={() => trackCategoryView(slot)}
                  >
                    {slot.label}
                  </TriggerLink>
                ) : (
                  <TriggerButton
                    type="button"
                    aria-haspopup="true"
                    $isActive={slot.isActive}
                    $isOpen={isOpen}
                    data-testid={`category-mega-nav-slot-${slot.id}`}
                    {...triggerProps}
                  >
                    {slot.label}
                  </TriggerButton>
                )}
                {isOpen && (
                  <Panel {...getPanelProps()}>
                    <MegaNavPanel
                      slot={slot}
                      onLinkClick={handleLinkClick}
                    />
                  </Panel>
                )}
              </BarItem>
            );
          })}
        </Bar>
        <MegaNavSeoLinks slots={slots} />
      </Root>
    </>
  );
};
